import { HeartFilledIcon } from "@/common/images/HeartFilledIcon"
import { HeartIcon } from "@/common/images/HeartIcon"
import { MarkerData } from "@/common/types"
import { useSnackbar } from "@/contexts/SnackbarContext"
import LargeEventDialog from "@/dialogs/events/LargeEventDialog"
import EventSeoMeta from "@/components/eventDetails/EventSeoMeta"
import LoginDialog from "@/dialogs/events/LoginDialog"
import useFavoriteEvent from "@/hooks/api/events/useFavoriteEvent"
import useDialogState from "@/hooks/useDialogState"
import { useGetMe } from "@/hooks/useGetMe"
import useIsMobile from "@/hooks/useIsMobile"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import shareIcon from "@/components/mapBox/images/copy_icon.png"

const InfoWindowDesign = ({
  activeMarker,
  closeInfoWindow,
}: //   setActiveMarker,
{
  activeMarker: MarkerData | null
  closeInfoWindow: () => void
}) => {
  const [markerData, setMarkerData] = useState(activeMarker)
  const isMobile = useIsMobile()
  const { data: me } = useGetMe()
  const [searchParams] = useSearchParams()
  const { mutateAsync: favoriteEvent } = useFavoriteEvent()
  const { openDialog, ...dialogState } = useDialogState({})
  const {
    openDialog: openLoginDialog,
    closeDialog: closeLoginDialog,
    isOpen: isLoginOpen,
  } = useDialogState({})
  const { showSnackbar } = useSnackbar()
  const navigate = useNavigate()

  searchParams.set("eventId", `${activeMarker?.id}`)

  const handleFavoriteEvent = () => {
    favoriteEvent(Number(activeMarker?.id)).then((res) => {
      if (!markerData) return
      if (res.message === "Event favorited") {
        setMarkerData({ ...markerData, favorited: true })
        showSnackbar("Event Favorited", "success")
      } else {
        showSnackbar("Event removed from favorites", "error")
        setMarkerData({ ...markerData, favorited: false })
      }
    })
  }
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (me.length) {
      handleFavoriteEvent()
    } else {
      openLoginDialog({})
    }
  }
  // matches the query-string deep link the app already uses to open an event -
  // keep click-through and share links pointing at the same working URL shape
  const buildEventDeepLink = (eventId: number | string | undefined) => {
    return `${location.pathname}?eventId=${eventId}&details=true`
  }

  const shareUrl = `${window.location.origin}${buildEventDeepLink(activeMarker?.id)}`

  const likeAndShareButtonStyling = {
    backgroundColor: "white",
    width: "30px",
    height: "30px",
    padding: ".5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: ".2rem",
    marginRight: ".2rem",
  }

  const copyToClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      showSnackbar("Copied link to event!", "success")
    } catch (err) {
      console.error("could not copy", err)
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const details = searchParams.get("details")
    if (details && !dialogState.isOpen) {
      openDialog({})
    }
  }, [openDialog, dialogState.isOpen])

  return (
    <Box
      sx={{
        width: "350px",
        height: "260px",
        cursor: "pointer",
      }}
    >
      <style>{`
        .gm-style .gm-style-iw-d::-webkit-scrollbar-track,
        .gm-style .gm-style-iw-d::-webkit-scrollbar-track-piece,
        .gm-style .gm-style-iw-c,
        .gm-style .gm-style-iw-t::after {
          padding: 0;
        }
        .gm-style-iw-c {
          max-width: none !important;
          width: auto !important;
          min-width: 300px !important; /* Adjust as needed */
        }
        .gm-style-iw-chr {
          position: absolute;
          visibility: hidden;
        }
        .gm-style-iw-chr button span {
          background-color: white;
        }
        .gm-style-iw-tc {
          display: none;
        }
      `}</style>
      <Box
        sx={{
          width: "350px",
          height: "260px",
          margin: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#CCCCCC",
          position: "relative",
          overflow: isMobile ? "hidden" : "visible",
        }}
      >
        <Stack
          direction="row"
          gap={1}
          style={{
            position: "absolute",
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            width: isMobile ? "90%" : "100%",
            padding: ".3rem",
            margin: ".3rem",
            zIndex: "+5",
          }}
        >
          <IconButton
            style={likeAndShareButtonStyling}
            onClick={(e) => {
              e.stopPropagation()
              copyToClipBoard()
            }}
          >
            <img
              src={shareIcon}
              style={{ width: "17px", height: "17px" }}
              alt="share event"
            />
          </IconButton>
          <IconButton
            style={likeAndShareButtonStyling}
            onClick={(e) => {
              e.stopPropagation() // ✅ stop click from bubbling
              handleFavoriteClick(e)
            }}
          >
            {markerData?.favorited ? (
              <HeartFilledIcon />
            ) : (
              <HeartIcon map={true} />
            )}
          </IconButton>
        </Stack>
        <Box onClick={(e) => {
          e.stopPropagation()
          navigate(buildEventDeepLink(activeMarker?.id))
        }}
         sx={{ cursor: "pointer" }}
         >
          <img
            src={
              activeMarker?.event_images?.length
                ? activeMarker?.event_images[0].image_url
                : ""
            }
            alt={String(activeMarker?.label)}
            style={{
              width: "100%",
              height: "200px",
              display: "block",
              objectFit: "cover",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
            }}
          />

          <Box
            sx={{
              paddingTop: ".3rem",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              position: "absolute",
              width: "100%",
              bottom: "0",
              backgroundImage:
                "linear-gradient(0deg, rgba(2,0,36,1) 0%, rgba(0,0,0,1) 85%, rgba(0,0,0,0) 100%)",
            }}
          >
            <Stack direction="row" sx={{ alignItems: "flex-end" }}>
              <Stack spacing={0.5} sx={{ padding: ".9rem" }}>
                <Typography
                  variant="h5"
                  style={{
                    marginBottom: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {activeMarker?.event_types?.[0].icon_link && (
                    <Box
                      component="img"
                      src={activeMarker?.event_types?.[0].icon_link}
                      sx={{
                        height: "1.3rem",
                        paddingRight: ".5rem",
                        filter:
                          "invert(100%) sepia(0%) saturate(2%) hue-rotate(64deg) brightness(109%) contrast(101%);",
                      }}
                    />
                  )}
                  {activeMarker?.label}
                </Typography>
                <Stack direction="row">
                  <Typography
                    variant="body2"
                    sx={{ padding: 0, lineHeight: ".8rem" }}
                  >
                    {activeMarker?.location_title},{" "}
                    {dayjs(activeMarker?.event_start_timestamp).format(
                      "MMM D - h A"
                    )}
                  </Typography>
                </Stack>
              </Stack>

              <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "end" }}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    closeInfoWindow()
                  }}
                  sx={{
                    color: "white",
                    height: "25px",
                    margin: ".5rem",

                    border: "1px solid white",
                  }}
                >
                  <Typography variant="h5">X</Typography>
                </IconButton>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
      {dialogState.isOpen === true && (
        <>
          <EventSeoMeta eventId={Number(activeMarker?.id)} />
          <LargeEventDialog {...dialogState} eventId={Number(activeMarker?.id)} />
        </>
      )}
      <LoginDialog
        closeDialog={closeLoginDialog}
        isOpen={isLoginOpen}
        message="Log in to favorite events!"
      />
    </Box>
  )
}

export default InfoWindowDesign
